<template>
  <div id="tenantEdit">
    <el-row class="header-row">
      <el-col :span="24">
        <h3 class="el-page-header">Tenant Details</h3>
        <el-breadcrumb separator="/">
          <el-breadcrumb-item :to="{ path: '/' }">Home</el-breadcrumb-item>
          <el-breadcrumb-item :to="{ path: '/tenants' }">Tenants</el-breadcrumb-item>
          <el-breadcrumb-item>Tenant Detail</el-breadcrumb-item>
        </el-breadcrumb>
      </el-col>
    </el-row>
    <el-card>
      <div v-if="initializing" v-loading="initializing" class="top-margin" element-loading-spinner="atom-audit-loader" style="min-height: 150px;"></div>
      <div v-if="!initializing" v-loading="loading" element-loading-spinner="atom-audit-loader" style="min-height: 150px;">
        <el-alert v-if="error" type="error" show-icon :closable="true" @close="error = null" :title="error" style="margin-bottom: 12px;"></el-alert>
        <el-alert v-if="success" type="success" show-icon :closable="false" :title="success" style="margin-bottom: 12px;"></el-alert>
        <el-form ref="tenantForm" :model="tenant" :rules="rules" size="mini" label-position="left" label-width="200px">
          <el-row :gutter="40">
            <el-col :span="12">
              <h4 class="heading">Basic Information</h4>
              <el-form-item label="Tenant Name" prop="tenantName" required>
                <el-input v-model="tenant.tenantName" size="mini" type="text"></el-input>
              </el-form-item>
              <el-form-item label="Tenant GUID">
                <el-input v-model="tenant.tenantGuid" size="mini" type="text" disabled></el-input>
              </el-form-item>
              <el-form-item label="Database Name">
                <el-input v-model="tenant.dbName" size="mini" type="text" disabled></el-input>
              </el-form-item>
              <el-form-item label="Storage Account Name">
                <el-input v-model="tenant.storageAccountName" size="mini" type="text" disabled></el-input>
              </el-form-item>
              <el-form-item label="Active">
                <el-switch v-model="tenant.active" active-color="#13ce66" inactive-color="#ff4949"></el-switch>
              </el-form-item>
              <el-form-item label="Is Client">
                <el-switch v-model="tenant.isClient" active-color="#13ce66" inactive-color="#ff4949"></el-switch>
              </el-form-item>
              <el-form-item label="Contract End Date">
                <el-date-picker v-model="tenant.contractEndDate" type="date" placeholder="Select date" format="MM/dd/yyyy" value-format="yyyy-MM-dd" size="mini" style="width: 100%;"> </el-date-picker>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <h4 class="heading">License Information</h4>
              <el-form-item label="Number of Auditors">
                <el-input-number v-model="tenant.numberAuditors" :min="0" size="mini" style="width: 100%;"></el-input-number>
              </el-form-item>
              <el-form-item label="Number of Coders">
                <el-input-number v-model="tenant.numberCoders" :min="0" size="mini" style="width: 100%;"></el-input-number>
              </el-form-item>
              <el-form-item label="Number of Read Only">
                <el-input-number v-model="tenant.numberReadOnly" :min="0" size="mini" style="width: 100%;"></el-input-number>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="40" style="margin-top: 20px;">
            <el-col :span="12">
              <h4 class="heading">Trucode Settings</h4>
              <el-form-item label="Trucode Enabled">
                <el-switch v-model="tenant.trucodeEnabled" active-color="#13ce66" inactive-color="#ff4949"></el-switch>
              </el-form-item>
              <el-form-item label="Trucode Client ID">
                <el-input v-model="tenant.trucodeClientId" size="mini" type="text"></el-input>
              </el-form-item>
              <el-form-item label="Trucode Customer ID">
                <el-input v-model="tenant.trucodeCustomerId" size="mini" type="text"></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <h4 class="heading">Feature Flags</h4>
              <el-form-item label="Coder Enhanced Enabled">
                <el-switch v-model="tenant.coderEnhancedEnabled" active-color="#13ce66" inactive-color="#ff4949"></el-switch>
              </el-form-item>
              <el-form-item label="Pro Risk Analysis Enabled">
                <el-switch v-model="tenant.proRiskAnalysisEnabled" active-color="#13ce66" inactive-color="#ff4949"></el-switch>
              </el-form-item>
              <h4 class="heading" style="margin-top: 20px;">SSO Settings</h4>
              <el-form-item label="SSO Enabled">
                <el-switch v-model="tenant.ssoEnabled" active-color="#13ce66" inactive-color="#ff4949"></el-switch>
              </el-form-item>
              <el-form-item label="SSO Connection">
                <el-input v-model="tenant.ssoConnection" size="mini" type="text"></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row style="margin-top: 20px;">
            <el-col :span="24">
              <el-button class="hiaButton" plain round type="primary" size="mini" :disabled="loading" @click="saveTenant">
                Save
              </el-button>
            </el-col>
          </el-row>
        </el-form>
      </div>
    </el-card>
  </div>
</template>

<script>
  import { mapState } from 'vuex'
  import axios from '@/utilities/axios-global'

  export default {
    name: 'TenantEdit',
    data() {
      return {
        initializing: true,
        loading: false,
        error: null,
        success: null,
        rules: {
          tenantName: [
            { required: true, message: 'Tenant Name is required', trigger: 'blur' },
            { min: 1, message: 'Tenant Name cannot be empty', trigger: 'blur' }
          ]
        },
        tenant: {
          tenantId: null,
          tenantName: '',
          tenantGuid: null,
          dbName: '',
          active: true,
          isClient: true,
          contractEndDate: null,
          numberAuditors: 0,
          numberCoders: 0,
          numberReadOnly: 0,
          trucodeEnabled: false,
          trucodeClientId: '',
          trucodeCustomerId: '',
          coderEnhancedEnabled: false,
          proRiskAnalysisEnabled: false,
          ssoEnabled: false,
          ssoConnection: '',
          storageAccountName: '',
          storageAccountKey: null
        }
      }
    },
    computed: {
      ...mapState('user', ['baseApiUrl', 'currentTenantGuid'])
    },
    methods: {
      async loadTenant() {
        this.initializing = true
        this.error = null
        const tenantId = this.$route.params.id

        if (!tenantId) {
          this.error = 'Tenant ID is required'
          this.initializing = false
          return
        }

        try {
          const response = await axios.get(`${this.baseApiUrl}/tenantAdmin/tenants/${tenantId}`)
          this.tenant = response.data
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error loading tenant:', error)
          this.error = error?.response?.data?.message || 'Failed to load tenant'
        } finally {
          this.initializing = false
        }
      },
      async saveTenant() {
        // Check tenantName directly to prevent PUT without it
        if (!this.tenant.tenantName || this.tenant.tenantName.trim() === '') {
          this.error = 'Tenant Name is required'
          return
        }

        // Validate form before submitting
        this.$refs.tenantForm.validate(async valid => {
          if (!valid) {
            this.error = 'Please correct the form errors'
            return false
          }

          this.loading = true
          this.error = null
          this.success = null

          try {
            const tenantId = this.$route.params.id
            await axios.put(`${this.baseApiUrl}/tenantAdmin/tenants/${tenantId}`, this.tenant)
            this.success = 'Tenant updated successfully'
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error saving tenant:', error)
            this.error = error?.response?.data?.message || 'Failed to save tenant'
          } finally {
            this.loading = false
          }
        })
      }
    },
    created() {
      this.loadTenant()
    },
    watch: {
      $route() {
        this.loadTenant()
      }
    }
  }
</script>

<style scoped>
  .heading {
    margin-bottom: 15px;
    font-weight: 500;
  }
</style>
